import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DocumentItem from "../../../Components/Masters/DocumentItem";
import { getMonthName } from "../../../utils/getMonthName";
import { truncateNumber } from "../../../utils/common";

/** Matches "Waste Water", "Wastewater", etc. */
const isWastewaterWasteCategory = (wasteCategory) =>
  String(wasteCategory ?? "")
    .toLowerCase()
    .replace(/\s+/g, "") === "wastewater";

const isDensityRequiredWasteType = (wasteType) => {
  const t = String(wasteType ?? "").trim();
  return t === "Effluent" || t === "Sewage";
};

const requiresDensityAndVolumetricRow = (fd) => {
  if (!fd) return false;
  return (
    isWastewaterWasteCategory(fd.wasteCategory) ||
    isDensityRequiredWasteType(fd.wasteType)
  );
};

/** Same rules as AddData: density for wastewater category or Effluent / Sewage waste types. */
const shouldShowDensityInView = (data) => requiresDensityAndVolumetricRow(data);

const DENSITY_UNIT_LABEL = "Tonnes/m\u00b3";

/** Table cell padding — applied on `Box` `table-cell` (no MUI TableCell theme overrides). */
const TABLE_BORDER = "1px solid #ddd";
const CELL_PAD = "12px 10px";

const tableSx = {
  display: "table",
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "collapse",
  borderTop: TABLE_BORDER,
  borderLeft: TABLE_BORDER,
  borderRight: TABLE_BORDER,
  backgroundColor: "#fff",
};

const tableRowSx = { display: "table-row" };

const labelCellSx = {
  display: "table-cell",
  borderRight: TABLE_BORDER,
  borderBottom: TABLE_BORDER,
  padding: CELL_PAD,
  width: "30%",
  maxWidth: 200,
  fontWeight: 700,
  fontSize: "0.875rem",
  color: "#000",
  verticalAlign: "top",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

const valueCellSx = {
  display: "table-cell",
  borderBottom: TABLE_BORDER,
  padding: CELL_PAD,
  width: "70%",
  minWidth: 0,
  fontSize: "0.875rem",
  color: "#000",
  verticalAlign: "top",
  textAlign: "left",
  backgroundColor: "#fff",
  boxSizing: "border-box",
};

const valueInnerSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function formatCreatedAtDisplay(date) {
  if (!date) return "-";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ViewData({
  title,
  inputData,
  element,
  viewData,
  setViewData,
  handleEdit,
  setData,
  companyAdmin,
}) {
  const [filteredData, setFilteredData] = useState({});

  useEffect(() => {
    if (element) {
      setFilteredData(element);
    }
  }, [element]);

  const renderQuantity = (value) => (
    <Box component="span" sx={{ display: "inline", minWidth: 0 }}>
      {value?.vendorLegalName ? (
        <Box component="span" sx={{ mr: 0.5 }}>
          {value.vendorLegalName}{" "}
        </Box>
      ) : null}
      <span>{truncateNumber(value.value) || 0} </span>
      <span>{value.unit}</span>
    </Box>
  );

  const list = () => (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: "26.5rem",
        maxWidth: "100%",
        height: "100vh",
        justifyContent: "space-between",
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "3%",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton
          onClick={() => {
            setViewData(false);
            setData({});
          }}
          sx={{ color: "black" }}
        >
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{ p: 3, width: "100%", backgroundColor: "#fff", overflow: "auto" }}
      >
        <Typography
          variant="h6"
          sx={{
            textDecoration: "underline",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {title ? title : "Purchased Goods & Services Details"}
        </Typography>

        <Box role="table" sx={tableSx}>
          <Box role="row" sx={tableRowSx}>
            <Box role="columnheader" sx={labelCellSx}>
              Date
            </Box>
            <Box role="cell" sx={valueCellSx}>
              <Box sx={valueInnerSx}>
                {formatCreatedAtDisplay(element?.createdAt)}
              </Box>
            </Box>
          </Box>
          {inputData.map((input) => {
            const value = filteredData?.[input.fieldName];

            if (input.fieldName === "documents") return null;

            if (input.fieldName === "density") {
              if (!shouldShowDensityInView(filteredData)) return null;
              const d = filteredData?.density;
              const num = Number(d);
              const hasValue =
                d !== undefined &&
                d !== null &&
                d !== "" &&
                String(d).trim() !== "" &&
                !Number.isNaN(num);
              const densityDisplay = hasValue
                ? `${truncateNumber(num)} ${DENSITY_UNIT_LABEL}`
                : "-";

              return (
                <Box key={input.fieldName} role="row" sx={tableRowSx}>
                  <Box role="columnheader" sx={labelCellSx}>
                    {input.title}
                  </Box>
                  <Box role="cell" sx={valueCellSx}>
                    <Box sx={valueInnerSx}>{densityDisplay}</Box>
                  </Box>
                </Box>
              );
            }

            const displayValue =
              filteredData?.wasteManagementMethod === "Landfilling" &&
              input.fieldName === "quantityOfWasteManaged"
                ? "-"
                : input.fieldName === "consumptionPeriod"
                  ? `${getMonthName(value?.month) ?? ""} ${value?.year ?? ""}`.trim() ||
                    "-"
                  : value?.unit
                    ? renderQuantity(value)
                    : value?.vendorLegalName || value?.value || value || "-";

            return (
              <Box key={input.fieldName} role="row" sx={tableRowSx}>
                <Box role="columnheader" sx={labelCellSx}>
                  {input.title}
                </Box>
                <Box role="cell" sx={valueCellSx}>
                  <Box
                    sx={
                      typeof displayValue === "string" ||
                      typeof displayValue === "number"
                        ? valueInnerSx
                        : { minWidth: 0, maxWidth: "100%" }
                    }
                  >
                    {displayValue}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Documents (optional)
          </Typography>
          {element && element?.documents?.length > 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ width: "100%" }}
            >
              <Box display="flex" flexWrap="wrap" gap={1}>
                {element?.documents
                  .filter((url) =>
                    ["png", "jpg", "jpeg"].includes(
                      url.split(".").pop().toLowerCase()
                    )
                  )
                  .map((url, index) => (
                    <DocumentItem key={`image-${index}`} url={url} />
                  ))}
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {element?.documents
                  .filter(
                    (url) =>
                      !["png", "jpg", "jpeg"].includes(
                        url.split(".").pop().toLowerCase()
                      )
                  )
                  .map((url, index) => (
                    <DocumentItem key={`file-${index}`} url={url} />
                  ))}
              </Box>
            </Box>
          ) : (
            <Typography>-</Typography>
          )}
        </Box>

        {!companyAdmin && (
          <Button
            variant="contained"
            sx={{ mt: 2, backgroundColor: "#0a3f73", textTransform: "none" }}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={viewData}
      onClose={() => {
        setViewData(false);
        setData({});
      }}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {list()}
    </Drawer>
  );
}

export default ViewData;
